import { axiosService } from "@/services/AxiosService";



/**
 * @typedef {Object} HorseTraining
 * @property {number} id - ID del record dell'addestramento
 * @property {number} horse_id - ID del cavallo associato
 * @property {number} training_id - ID del tipo di addestramento
 * @property {number} repetitions - Numero di ripetizioni dell'addestramento
 * @property {string} from_date - Data di inizio dell'addestramento
 * @property {string} to_date - Data di fine dell'addestramento
 * @property {string} note - Note aggiuntive sull'addestramento
 * @property {string} free_field - Campo libero per informazioni aggiuntive
 * @property {number} ranking - Classifica o valutazione dell'addestramento
 * @property {string} modified - Data di modifica del record
 * @property {string} created - Data di creazione del record
 */

 /**
 * @typedef {Object} Training
 * @property {number} id - ID del record dell'addestramento
 * @property {string} name - Nome dell'addestramento
 * @property {number} training_type_id - ID del tipo di addestramento
 */


/**
 * @typedef {Object} Exercise
 * @property {number} id - ID del record dell'esercizio
 * @property {string} name - Nome dell'esercizio
 */

/**
 * @typedef {Object} TrainingType
 * @property {number} id - ID del record del tipo di addestramento
 * @property {string} name - Nome del tipo di addestramento
 */


/**
 * Servizio per gestire le informazioni dei cavalli.
 */
class HorsesTrainingService {

    abortController = null;

    constructor() {
        this.axiosInstance = axiosService.getInstance(); // Usa l'istanza di AxiosService
    }

    

    /**
     * 
     * @returns {Promise<HorseTraining[]>}
     * @throws {Error}
     * status -> inprogress - future - executed
     */
    async getHorseTrainings(page, search = '', params = {}, length = 10) {
        try {
            const response = await this.axiosInstance.post('/horse-trainings/get-horse-trainings-given-horse-id', {page, length, search, ...params});

            if (response.status !== 200) {
                throw new Error('Failed to fetch horse training info');
            }

            /** @type {HorseTraining[]} */
            return response.data;
        } catch (error) {
            console.error('Error fetching horse training info:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }

     /**
     * 
     * @returns {Promise<HorseTraining>} - 
     * @throws {Error} - 
     */
     async getHorseTrainingById(id) {
        try {
            const response = await this.axiosInstance.post('/horse-trainings/get-horse-training-given-id', {horse_training_id: id});

            if (response.status !== 200) {
                throw new Error('Failed to fetch horse training info');
            }

            /** @type {HorseTraining} */
            return response.data.data;
        } catch (error) {
            console.error('Error fetching horse training info:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }

    /**
     * 
     * @returns {Promise<HorseTraining>} 
     * @throws {Error} 
     */
    async saveHorseTraining(data) {
        try {
            
            const response = await this.axiosInstance.post('/horse-trainings/save-horse-training', data);

            if (response.status !== 200) {
                throw new Error('Failed to save horse training data');
            }

            /** @type {HorseTraining} */
            return response.data.data.result;
        } catch (error) {
            console.error('Error saving horse training:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }


    /**
     * 
     * @returns {Promise<Training[]>}
     * @throws {Error}
     */
    async getTrainings() {
        try {
            const response = await this.axiosInstance.post('/trainings/get-trainings');

            if (response.status !== 200) {
                throw new Error('Failed to fetch trainings info');
            }

            /** @type {Training[]} */
            return response.data.data.trainings;
        } catch (error) {
            console.error('Error fetching trainings info:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }

    /**
     * 
     * @returns {Promise<TrainingType[]>}
     * @throws {Error}
     */
    async getTrainingTypes() {
        try {
            const response = await this.axiosInstance.post('/training-types/get-training-types');

            if (response.status !== 200) {
                throw new Error('Failed to fetch training type info');
            }

            /** @type {TrainingType[]} */
            return response.data.data.training_types;
        } catch (error) {
            console.error('Error fetching training types info:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }

     /**
     * 
     * @returns {Promise<Training>} 
     * @throws {Error} 
     */
     async addTraining(data) {
        try {
            
            const response = await this.axiosInstance.post('/trainings/add-training', data);

            if (response.status !== 200) {
                throw new Error('Failed to save training data');
            }

            /** @type {Training} */
            return response.data.data.result;
        } catch (error) {
            console.error('Error saving training:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }

    /**
     * 
     * @returns {Promise<TrainingType>} 
     * @throws {Error} 
     */
     async addTrainingType(data) {
        try {
            
            const response = await this.axiosInstance.post('/training-types/add-training-type', data);

            if (response.status !== 200) {
                throw new Error('Failed to save training type data');
            }

            /** @type {TrainingType} */
            return response.data.data.result;
        } catch (error) {
            console.error('Error saving training type:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }

    /**
     * 
     * @returns {Promise<Exercise[]>}
     * @throws {Error}
     */
    async getExercises() {
        try {
            const response = await this.axiosInstance.post('/exercises/get-exercises');

            if (response.status !== 200) {
                throw new Error('Failed to fetch exercises info');
            }

            /** @type {Exercise[]} */
            return response.data.data.exercises;
        } catch (error) {
            console.error('Error fetching exercises info:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }

    /**
     * 
     * @returns {Promise<Exercise>} 
     * @throws {Error} 
     */
    async addExercise(data) {
        try {
            
            const response = await this.axiosInstance.post('/exercises/add-exercise', data);

            if (response.status !== 200) {
                throw new Error('Failed to save exercise data');
            }

            /** @type {Exercise} */
            return response.data.data.result;
        } catch (error) {
            console.error('Error saving exercise:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }
    
}

export const horsesTrainingService = new HorsesTrainingService();
